import { differenceInCalendarDays } from "date-fns";
import moment from "moment-timezone";
import { ISitePackageInfo, ISiteMap } from "../models/Sites";

export type SitePackageThresholdBucket =
  | "3mo"
  | "2mo"
  | "1mo"
  | "2wk"
  | "1wk"
  | "expired"
  | "noEndDate";

const DAY_MS = 24 * 60 * 60 * 1000;
const NO_END_DATE_REPEAT_MS = 7 * DAY_MS;
const NO_END_DATE_SEEN_PREFIX = "noEndDate:";

export type SitePackageStatusColor = "green" | "yellow" | "orange" | "red" | "na";

export interface ISitePackageRow extends ISitePackageInfo {
  siteId: string;
  timezone?: string;
  daysLeft: number | null;
}

// The calendar day an instant falls on at the site, as a local-midnight Date so date-fns can
// compare and format it. Sites without a (known) time zone fall back to the viewer's own day.
export const toSiteDay = (epochMs: number, timezone?: string): Date => {
  if (!timezone || !moment.tz.zone(timezone)) {
    return new Date(epochMs);
  }
  const siteTime = moment.tz(epochMs, timezone);
  return new Date(siteTime.year(), siteTime.month(), siteTime.date());
};

// The site's UTC offset at that moment (e.g. "UTC+13", "UTC-4", "UTC+5:30"), only when the site's
// clock differs from the viewer's; otherwise null, since the date reads the same for both.
export const getSiteUtcOffsetLabel = (epochMs: number, timezone?: string): string | null => {
  if (!timezone || !moment.tz.zone(timezone)) {
    return null;
  }
  const offset = moment.tz(epochMs, timezone).utcOffset();
  if (offset === moment(epochMs).utcOffset()) {
    return null;
  }
  const sign = offset < 0 ? "-" : "+";
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}`;
};

export const getDaysLeft = (
  activePeriodEnd: number | null | undefined,
  timezone?: string,
  now: number = Date.now()
): number | null => {
  if (activePeriodEnd == null) {
    return null;
  }
  return differenceInCalendarDays(toSiteDay(activePeriodEnd, timezone), toSiteDay(now, timezone));
};

export const getThresholdBucket = (
  daysLeft: number | null
): SitePackageThresholdBucket | null => {
  if (daysLeft === null || daysLeft > 90) {
    return null;
  }
  if (daysLeft <= 0) {
    return "expired";
  }
  if (daysLeft <= 7) {
    return "1wk";
  }
  if (daysLeft <= 14) {
    return "2wk";
  }
  if (daysLeft <= 30) {
    return "1mo";
  }
  if (daysLeft <= 60) {
    return "2mo";
  }
  return "3mo";
};

// Enforced sites without an end date get their own bucket, matching the renewal email.
export const getSiteBucket = (row: ISitePackageRow): SitePackageThresholdBucket | null =>
  row.activePeriodEnd == null ? "noEndDate" : getThresholdBucket(row.daysLeft);

// What gets stored as "seen" for a site. No-end-date sites keep the time they were seen so the
// popup can come back weekly, like the email, until an end date is set.
export const toSeenValue = (bucket: SitePackageThresholdBucket, now: number = Date.now()): string =>
  bucket === "noEndDate" ? `${NO_END_DATE_SEEN_PREFIX}${now}` : bucket;

// A dated site's bucket only moves toward "expired" as time passes, so any bucket that differs from
// the last one shown means either a tighter threshold was crossed or the package was renewed/edited.
export const shouldShowPopup = (
  current: SitePackageThresholdBucket | null,
  lastShown: string | null | undefined,
  now: number = Date.now()
): boolean => {
  if (current === null) {
    return false;
  }
  if (current === "noEndDate") {
    if (!lastShown || !lastShown.startsWith(NO_END_DATE_SEEN_PREFIX)) {
      return true;
    }
    const seenAt = Number(lastShown.slice(NO_END_DATE_SEEN_PREFIX.length));
    return Number.isNaN(seenAt) || now - seenAt >= NO_END_DATE_REPEAT_MS;
  }
  return current !== lastShown;
};

export const getStatusColor = (daysLeft: number | null): SitePackageStatusColor => {
  if (daysLeft === null) {
    return "na";
  }
  if (daysLeft < 30) {
    return "red";
  }
  if (daysLeft < 60) {
    return "orange";
  }
  if (daysLeft < 90) {
    return "yellow";
  }
  return "green";
};

const toRows = (
  sitesPackages: { [siteId: string]: ISitePackageInfo | undefined },
  allSites: ISiteMap
): ISitePackageRow[] =>
  Object.keys(sitesPackages).map(siteId => {
    const info = sitesPackages[siteId] as ISitePackageInfo;
    const timezone = allSites[siteId]?.timezone;
    return {
      ...info,
      siteId,
      timezone,
      daysLeft: getDaysLeft(info.activePeriodEnd, timezone),
    };
  });

// All sites the user can see, unfiltered — used by the "Account Subscription Status" popup.
export const getAllSitePackageRows = (
  sitesPackages: { [siteId: string]: ISitePackageInfo | undefined },
  allSites: ISiteMap
): ISitePackageRow[] => toRows(sitesPackages, allSites);

// Sites that can surface in the "Subscription Renewal Required" popup: enforced, and either
// without an end date or expiring within 3 months (i.e. inside the threshold ladder).
export const getSitesRequiringAttention = (
  sitesPackages: { [siteId: string]: ISitePackageInfo | undefined },
  allSites: ISiteMap
): ISitePackageRow[] =>
  toRows(sitesPackages, allSites).filter(row => {
    if (!row.isEnforcePackage) {
      return false;
    }
    if (row.activePeriodEnd == null) {
      return true;
    }
    if (row.activePeriodStart == null) {
      return false;
    }
    return getThresholdBucket(row.daysLeft) !== null;
  });

// Most urgent first: fewer days left sorts higher, so expired packages lead the list.
export const sortByUrgency = (rows: ISitePackageRow[]): ISitePackageRow[] =>
  [...rows].sort(
    (a, b) => (a.daysLeft ?? Number.POSITIVE_INFINITY) - (b.daysLeft ?? Number.POSITIVE_INFINITY)
  );
